import { PlayerForSaveDto } from '@/api/models';
import styles from './PlayerContract.module.scss';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Contract, ContractItems, CurrentContract } from '@/api/models/PlayerContractHistory';
import { postUpsertPlayerContract } from '@/api/endpoints/postUpsertPlayerContract';
import { PlayerContractPayload } from '@/api/models/PlayerContractPayload';
import Checkbox from '@/common/Checkbox';
import { PlayerService } from '@/service';

class FormData {
  startDate? = '';
  endDate? = '';
  items? = [] as Array<string>;
}

interface IContractEditModalProps {
  player: PlayerForSaveDto | null;
  // eslint-disable-next-line no-unused-vars
  handleOnSave: (contract: Contract) => void;
  handleCancelButton: () => void;
  // eslint-disable-next-line no-unused-vars
  handleCloseButton: (newContract: CurrentContract) => void;
  contract: CurrentContract;
}

function Edit({ player, contract, handleOnSave, handleCloseButton, handleCancelButton }: IContractEditModalProps) {
  const [formData, setFormData] = useState(new FormData());
  const [formIsValid, setFormIsValid] = useState(false);

  useEffect(() => {
    let newFormData = new FormData();
    if (contract && contract.startDate) {
      newFormData = {
        startDate: dayjs(contract.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(contract.endDate).format('YYYY-MM-DD'),
        items: [...contract.items],
      };
    }
    setFormData(newFormData);
    validateForm(newFormData);
    // TODO: Fix use effect to removing this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDate = (date: string) => {
    setFormData({ ...formData, startDate: date });
    //setStartDate(date);
    date = date + 'T00:00:00';
    validateForm({ startDate: date });
  };

  const handleEndDate = (date: string) => {
    setFormData({ ...formData, endDate: date });
    date = date + 'T00:00:00';
    validateForm({ endDate: date });
  };

  const handleClearAllButton = () => {
    setFormData({ ...formData, items: [] });
    setFormIsValid(false);
  };

  const handleToggleItem = (item: string) => {
    if (itemIsSelected(item)) {
      let ix = formData.items?.indexOf(item) || 0;
      formData.items?.splice(ix, 1);
      setFormData({ ...formData, items: formData.items });
    } else {
      formData.items?.push(item);
      setFormData({ ...formData, items: formData.items });
    }
    validateForm({ items: formData.items });
  };

  const itemIsSelected = (item: string) => {
    return (formData.items || []).indexOf(item) > -1;
  };

  const handleSaveButton = async () => {
    if (!player) throw new Error('Missing player');
    if (formIsValid) {
      try {
        const contractPost: PlayerContractPayload = {
          playerId: player?.playerId || 0,
          contract: { startDate: formData.startDate || '', endDate: formData.endDate || '', items: formData.items || [] },
        };
        const response = await postUpsertPlayerContract(contractPost);
        const updatedContract = response?.contract;
        if (!updatedContract) throw new Error('Missing API response');
        await PlayerService.updatePlayerContract({ playerId: player.playerId, contract: updatedContract });
        handleOnSave(updatedContract);
        handleCloseButton(updatedContract as CurrentContract);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const validateForm = (form: FormData) => {
    let newFormData = { ...formData, ...form };
    let isValid = newFormData.startDate !== '' && newFormData.endDate !== '' && dayjs(newFormData.startDate).isBefore(dayjs(newFormData.endDate)) && (newFormData.items || []).length > 0;
    setFormIsValid(isValid);
  };

  return (
    <>
      <div className={styles.editHeading} data-testid="tourbag-contract-edit-form-root">
        Edit Contract
      </div>
      <div className={styles.playerName}>{player?.fullName || ''}</div>
      <div className={styles.editDateRow}>
        <div>
          <div>Start Date</div>
          <div>
            <input type="date" value={formData.startDate} onChange={(e) => handleStartDate(e.target.value)} className={styles.editDate} />
          </div>
        </div>
        <div>
          <div>End Date</div>
          <div>
            <input type="date" value={formData.endDate} onChange={(e) => handleEndDate(e.target.value)} className={styles.editDate} />
          </div>
        </div>
      </div>
      <div className={styles.editContractItems}>Contract Items</div>
      <div>
        <div className={styles.editItemRow}>
          {ContractItems.map((item, ix) => (
            <div key={ix} className={styles.editItem}>
              <Checkbox checked={itemIsSelected(item)} label={item} onClick={() => handleToggleItem(item)}></Checkbox>
            </div>
          ))}
        </div>
      </div>
      <hr />
      <div className={styles.editFooter}>
        <button type="button" className={styles.editClearAll} data-testid="tourbag-contract-clear-all-button" onClick={handleClearAllButton}>
          Clear All
        </button>
        <button type="button" className={styles.editCancelButton} data-testid="tourbag-contract-cancel-button" onClick={handleCancelButton}>
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.editSaveButton} ${!formIsValid ? styles['saveDisabled'] : ''}`}
          data-testid="tourbag-contract-save-button"
          onClick={handleSaveButton}
          disabled={!formIsValid}
        >
          Save
        </button>
      </div>
    </>
  );
}

export default Edit;
